<template>
  <div class="join-container">
    <address-section
      :order-num="orderId"
      :current-shipping="shippingAddress"
      :current-billing="billingAddress"
      :shipping-same-as-billing="shippingSameAsBilling"
      :pickup.sync="pickupComputed"
      :disabled="orderLocked"
    />

    <section
      ref="cardFormWrapper"
      class="payment-section"
    >
      <div class="section-title">
        <h2>Payment Method</h2>
      </div>
      <card-display
        @update-payer="updatePayer"
        @error="paymentMethodError"
      />
    </section>

    <section
      v-if="allowOrderNow && !orderId"
      class="order-one-now-section"
    >
      <div class="section-title">
        <h2>WHY WAIT! GET ONE NOW</h2>
      </div>
      <b-container>
        <b-row>
          <b-col cols="12">
            <div class="section-card">
              <div class="section-card-msg">
                In addition to receiving your Club Order on the next planned shipping date <div class="shipping-date">
                  {{ nextShippingDate }}
                </div> we will send you the first delivery immediately.
              </div>
              <div class="section-card-toggle">
                <swoggle
                  v-if="showOrderNow"
                  v-model="orderOneNowComputed"
                  label-on="Yes"
                  label-off="No"
                />
                <div
                  v-if="orderOneNowComputed"
                  class="text-danger"
                >
                  Your card will be charged straight away.
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </section>
    <section
      v-if="!orderId"
      class="dob-section"
    >
      <div class="section-title">
        <h2>Date Of Birth</h2>
      </div>
      <b-container>
        <b-row>
          <b-col cols="12">
            <div class="section-card">
              <b-form-group>
                <label>Please enter your date of birth</label>
                <no-ssr>
                  <datepicker
                    v-model="dateOfBirth"
                    :required="true"
                    :input-class="dateclass"
                    placeholder="DD MMM YYYY"
                    format="DD MMM YYYY"
                    :latest-year="new Date().getFullYear() - 18"
                  />
                </no-ssr>
              </b-form-group>
              <div class="section-card-msg">
                Click on the Calendar to choose your day, month and year to confirm you are over 18.
              </div>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </section>
    <somnambulist-ui-order-summary />
    <div
      ref="joinButton"
      :class="{'join-enabled': saveButtonEnabled }"
      class="join-button-container"
    >
      <div
        v-if="submitting"
        class="join-button"
      >
        <b-spinner variant="light" />
        <div class="in-progress-text">
          In Progress...
        </div>
      </div>
      <div
        v-else
        class="join-button"
        @click="() => (orderId) ? submitOrderChange() : trySubmit()"
      >
        <div class="join-text">
          {{ joinButtonText }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AddressSection from './AddressSection'
import CardDisplay from './CardDisplay'
import SomnambulistUIOrderSummary from './OrderSummary'
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import VueScrollTo from 'vue-scrollto'
import dayjs from 'dayjs'

const components = {
  'address-section': AddressSection,
  'card-display': CardDisplay,
  'somnambulist-ui-order-summary': SomnambulistUIOrderSummary
}
if (process.env.SERVER_BUILD !== 'ssr') {
  components['datepicker'] = () => import('@aspedia/date-picker')
}

export default {
  name: 'join',
  components,
  asyncData({ store }) {
    if (!this.$ssrRunMode) {
      return new Promise((resolve) => {
        const googlePlaceScriptURL = `https://maps.googleapis.com/maps/api/js?key=${store.state.somnambulistui.googleAddressesAPIKey}&libraries=places`
        if (!store.state.somnambulistui.googleAddressesAPIKey || typeof window === 'undefined' || document.querySelector('script[src="' + googlePlaceScriptURL + '"]')) {
          return resolve(true)
        }

        const googlePlaceScript = document.createElement('script')
        googlePlaceScript.setAttribute('src', googlePlaceScriptURL)
        // Sometimes we get "google is not defined" in the AddressForm mounted hook. Lets avoid that.
        googlePlaceScript.addEventListener('load', () => resolve(true))
        document.head.appendChild(googlePlaceScript)
      })
    }
  },
  data() {
    return {
      cardReadyToSubmit: false,
      submitting: false
    }
  },
  mounted() {
    if (typeof document !== 'undefined') {
      if (!this.payerValid && this.orderId) {
        VueScrollTo.scrollTo(this.$refs.cardFormWrapper)
      }
      else if (this.$store.state.somnambulistui.scrollTop) {
        VueScrollTo.scrollTo(document.querySelector('body'))
      }
      else if (this.$store.state.somnambulistui.scrollBottom) {
        VueScrollTo.scrollTo(this.$refs.joinButton)
      }
      this.$store.commit('somnambulistui/SET_SCROLL_TOP', false)
      this.$store.commit('somnambulistui/SET_SCROLL_BOTTOM', false)
    }
    this.$store.commit('SET_PAGE_SETTING', { key: 'hasHeader', value: false })

    // Setup Order now
    if (!this.orderId && this.getSelectedQuantityObject.allowOrderNow && this.getSelectedQuantityObject.orderNowDefaultOn) {
      this.SET_ORDER_ONE_NOW(true)
    }
  },
  computed: {
    ...mapGetters('somnambulistui', ['isCustomOrder', 'getCurrentStep', 'getLangString', 'getSelectedQuantityObject']),
    ...mapState('somnambulistui', ['googleAddresesAPIKey', 'pickup', 'orderId', 'shippingAddress', 'billingAddress', 'payer', 'shippingAddressValid', 'billingAddressValid', 'payerValid', 'shippingSameAsBilling', 'orderLocked', 'orderOneNow', 'shippingCalculated']),
    dateclass: function() {
      return (!this.dateOfBirthValid) ? 'form-control is-invalid dob-border' : 'form-control is-valid dob-border'
    },
    orderOneNowComputed: {
      get() {
        return this.orderOneNow
      },
      set(val) {
        this.SET_ORDER_ONE_NOW(val)
      }
    },
    dateOfBirth: {
      get() {
        return this.$store.state.somnambulistui.dateOfBirth
      },
      set(newValue) {
        this.$store.commit('somnambulistui/STORE_SONMANBULIST_DOB', newValue)
      }
    },
    pickupComputed: {
      get() {
        return this.$store.state.somnambulistui.pickup
      },
      set(val) {
        this.SET_PICKUP(val)
      }
    },
    joinButtonText() {
      if (this.orderId) return 'Save Changes'

      if (this.formsCompleted && this.cardDetailsSaved) return this.getLangString('submitOrderButton')

      if (this.formsCompleted && !this.cardDetailsSaved) return 'Please save Credit Card details'

      return this.getLangString('submitOrderValidationFailedButton')
    },
    formsCompleted() {
      return this.billingAddressValid && this.shippingAddressValid && this.dateOfBirthValid && this.shippingCalculated
    },
    cardDetailsSaved() {
      return (this.payerValid || this.cardReadyToSubmit)
    },
    saveButtonEnabled() {
      return this.formsCompleted && this.cardDetailsSaved
    },
    isLoggedIn() {
      return this.$store.getters['user/isLoggedIn']
    },
    dateOfBirthValid() {
      return (this.orderId) || (this.dateOfBirth && dayjs(this.dateOfBirth).isBefore(dayjs().subtract(18, 'years')))
    },
    dateOfBirthState() {
      return (this.dateOfBirthValid) ? null : 'invalid'
    },
    allowOrderNow() {
      return this.getSelectedQuantityObject.allowOrderNow
    },
    showOrderNow() {
      return this.allowOrderNow && this.getSelectedQuantityObject.showOrderNow
    },
    nextShippingDate() {
      let output = 'N/A'
      if (this.getSelectedQuantityObject && this.getSelectedQuantityObject.nextShippingDate) {
        let date = dayjs(this.getSelectedQuantityObject.nextShippingDate.split('T')[0])
        if (date.isValid()) {
          output = date.format('DD/MM/YYYY')
        }
      }
      return output
    }
  },
  destroyed() {
    if (this.isLoggedIn && typeof localStorage !== 'undefined') {
      localStorage.removeItem('somnambulistSelectedOrder')
    }
  },
  methods: {
    ...mapActions('somnambulistui', ['submitOrder', 'changeOrder']),
    ...mapMutations('somnambulistui', ['SET_ORDER_COMPLETED', 'SET_PICKUP', 'SET_BILLING_ADDRESS', 'SET_SHIPPING_ADDRESS', 'SET_PHONE_NUMBER', 'SET_PAYER', 'SET_SHIPPING_ADDRESS_VALID', 'SET_BILLING_ADDRESS_VALID', 'SET_PAYER_VALID', 'SET_SHIPPING_SAME_AS_BILLING', 'SET_ACCOUNT_EMAIL', 'SET_ORDER_ONE_NOW']),
    updatePayer: function(payerDetails) {
      this.SET_PAYER(payerDetails)
      this.SET_PAYER_VALID(true)
    },
    submitOrderChange() {
      if (this.formsCompleted && this.cardDetailsSaved && !this.submitting) {
        this.submitting = true
        if (this.payerValid) {
          this.continueOrderChange()
        }
      }
    },
    trySubmit() {
      if (this.formsCompleted && this.cardDetailsSaved && !this.submitting) {
        if (this.$store.state.somnambulistui.shippingSameAsBilling) {
          this.SET_SHIPPING_ADDRESS({
            ...this.$store.state.somnambulistui.billingAddress,
            phone: this.$store.state.somnambulistui.shippingAddress.phone,
            deliveryInstructions: this.$store.state.somnambulistui.shippingAddress.deliveryInstructions
          })
        }
        this.submitting = true
        this.submitOrder()
      }
    },
    submitOrder() {
      let p = Promise.resolve()
      if (this.$store.state.somnambulistui.updatePersonNid) {
        p = this.$store.dispatch('somnambulistui/updatePerson')
      }
      return p.then(_ => {
        return (this.orderId) ? this.continueOrderChange() : this.continueOrderSubmission()
      })
    },
    continueOrderChange() {
      return this.changeOrder({ orderId: this.orderId, payer: this.payer })
        .then(() => {
          const report = this.$store.getters['somnambulistui/getEventReportData']
          this.$eventHub.$emit('eventReport', { event: 'wine_club_order_changed', 'wine_club': report })
          this.$eventHub.$emit('resetSomnambulistOrders')
          this.$eventHub.$emit('statusMessage', {
            type: 'success',
            title: 'Order successfully submitted.',
            text: '',
            showClose: true,
            timeout: 3500
          })
        })
        .then(_ => {
          let uid = this.$store.getters['user/userid']
          this.$store.dispatch('user/refreshLogin')
          this.$router.push(`/user/${uid}/membership`)
        })
        .catch(() => {
          this.$eventHub.$emit('statusMessage', {
            type: 'error',
            title: 'Order failed to submit.',
            text: '',
            showClose: true,
            timeout: 3500 })
        })
    },
    continueOrderSubmission() {
      this.$store.dispatch('somnambulistui/submitOrder', {
        provider: this.payer.provider,
        token: this.payer.payerid,
        name: this.payer.name,
        pan: this.payer.pan,
        expiry_date: this.payer.expdate,
        dateOfBirth: this.dateOfBirth,
        pickup: this.pickup
      })
        .then(res => {
          if (res) {
            const report = this.$store.getters['somnambulistui/getEventReportData']
            this.$eventHub.$emit('eventReport', { event: 'wine_club_selection_submitted', 'wine_club': report })
            this.$eventHub.$emit('statusMessage', { type: 'success', title: 'Order successfully submitted.', text: '', showClose: true, timeout: 3500 })
            let uid = this.$store.getters['user/userid']
            this.$store.dispatch('user/refreshLogin')
            this.$router.push(`/user/${uid}/membership`)
          }
          else {
            throw new Error()
          }
        })
        .catch(_err => {
          this.$eventHub.$emit('statusMessage', { type: 'error', title: 'Order failed to submit.', text: '', showClose: true, timeout: 3500 })
        })
    },
    getErrorState(id) {
      return (this.errorBag.has(id, this.veeScope)) ? 'invalid' : null
    },
    hasErrors(id) {
      return this.errorBag.has(id, this.veeScope)
    },
    paymentMethodError: function(err) {
      const errors = err.message.split('::').map(err => `<li>${err}</li>`)
      this.$eventHub.$emit('statusMessage', {
        type: 'error',
        title: 'Order Payment Method Error.',
        text: `
          The following errors were encountered when processing the Payment Method for this order:<br/>
          <ul>${errors}</ul>
        `,
        showClose: true,
        timeout: 3500
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@scss/custom.scss';

.join-container {
  >.payment-section {
    margin: 6em 0 6em 0;
    flex-grow: 1;
    .section-title {
      margin: 0 0 2em 0;
      width: 100%;
      text-align: center;
    }
    .help-text {
      text-align: center;
      padding-bottom: 2em;
      border-bottom: 1px solid var(--main-border-color);
      margin-bottom: 2em;
    }
  }

  ::v-deep .order-one-now-section {
    margin: 0 0 6em 0;
    .section-title {
      margin: 0 0 2em 0;
      width: 100%;
      text-align: center;
    }
    .section-card {
      text-align: center;
      .section-card-msg {
        margin-bottom: 1em;
        .shipping-date {
          display: inline;
          font-weight: 700;
        }
      }
    }
  }

  .dob-border{
    border-color: #ced4da !important;
  }

  .dob-section {
    margin: 0 0 6em 0;
    .section-title {
      margin: 0 0 2em 0;
      width: 100%;
      text-align: center;
    }
    .section-card {
      text-align: center;
      input {
        text-align: center;
      }

      .vdp-datepicker {
          max-width: 100%;
          width: 16em;
          margin: auto;
          text-align: center;

          ::v-deep input {
            text-align: center;
          }

        ::v-deep .vdp-datepicker__calendar {
          max-width: 100%;
          text-align: left;
        }

        ::v-deep * {
          max-width: 100%;
        }
      }
      .section-card-msg {
        margin-top: 1em;
        font-size: 0.9em;
        opacity: 0.7;
      }
    }

  }

  ::v-deep .join-button-container {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 1em 1em 1em 1em;
    background: #fff;
    box-shadow: 0px -3px 10px 0px #0000000d;
    border-radius: 3px;
    z-index: 5;

    @include media-breakpoint-up (lg) { padding: 1em 1.25em 1em 1.25em; }

    .in-progress-text {
      margin-left: 0.75rem;
    }

    .join-button {
      cursor: pointer;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      background: var(--main-wine-club-complete-order-button-background);
      color: var(--main-wine-club-complete-order-button-text-color);
      font-size: 1.2em;
      padding: 0.75rem 1.25em;

      @include hover-focus {
        background: var(--main-wine-club-complete-order-button-highlight-background);
      }
    }

    &:not(.join-enabled) {
      .join-button {
        background: var(--main-wine-club-complete-order-button-background);
        filter: grayscale(100%);
        cursor: not-allowed;
      }
    }

  }
  ::v-deep .summary-heading {
    text-align: center;
    padding: 3em 0 0 0;
    background-color: var(--main-section-alternate-background-color);
  }
  min-height: 100vh;
  min-width: 100vw;
  color: rgba(26, 26, 26, 0.8);
  background-color: var(--main-section-background-color);
  display: flex;
  flex-direction: column;
  ::v-deep h2 {
    font-size: 1.8em;
    color: var(--main-text-heading-color);
  }
  ::v-deep h3 {
    font-size: 1.1em;
    color: var(--main-text-heading-color);
  }
  ::v-deep .required {
    label {
      &:after {
        content: ' *';
        color: var(--main-danger-color);
      }
    }
  }

  ::v-deep .section-card {
    &:not(.payment-card) {
      background-color: var(--main-card-background-color);
      padding: 1em;
      @include media-breakpoint-up('md') {
        padding: 2em;
      }
      border-radius: 3px 3px;
      box-shadow: var(--main-card-drop-shadow);
      .buttons {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        flex-grow: 0;
        flex-shrink: 0;
        .edit-button {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-left: 1em;
          @include media-breakpoint-down('sm') {
            margin: 1em 0 1em 1em;
          }
        }
        .clear-button {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-left: 1em;
          @include media-breakpoint-down('sm') {
            margin: 1em 0 1em 1em;
          }
        }
      }
      .add-button {
        text-align: center;
      }
      .close-button-container {
        margin: 0 0 1em 0;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        .close-button {
          color: var(--main-alpha-fill-colors);
          width: auto;
          cursor: pointer;
          transition: color 0.5s ease-in-out;
          &:hover {
            color: var(--main-danger-color);
          }
        }
      }
      .current-address-section {
        width: 100%;
        height: auto;
        display: flex;
        justify-content: space-between;
        align-content: center;
        flex-wrap: wrap;
      }

    }
  }
}
</style>
